import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const EditChannelDialog = ({ open, onClose, onEdit, channel }) => {
  const { t } = useTranslation(['common']);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with channel data when it changes
  useEffect(() => {
    if (channel) {
      setName(channel.name || '');
      setDescription(channel.description || '');
    }
  }, [channel]);

  const resetForm = () => {
    if (channel) {
      setName(channel.name || '');
      setDescription(channel.description || '');
    } else {
      setName('');
      setDescription('');
    }
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = t('comms.nameRequired');
    } else if (name.trim().length < 2) {
      newErrors.name = t('comms.nameTooShort');
    } else if (name.trim().length > 30) {
      newErrors.name = t('comms.nameTooLong');
    }
    
    if (description.length > 100) {
      newErrors.description = t('comms.descriptionTooLong');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !channel) return;
    
    try {
      setLoading(true);
      
      const channelData = {
        name: name.trim(),
        description: description.trim()
      };
      
      await onEdit(channel._id, channelData);
      handleClose();
    } catch (error) {
      console.error('Error updating channel:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('comms.editChannel')}
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('comms.editChannelDescription')}
        </Typography>
        
        <TextField
          autoFocus
          margin="dense"
          label={t('comms.channelName')}
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          disabled={loading}
          placeholder={t('comms.channelNamePlaceholder')}
          sx={{ mb: 2 }}
        />
        
        <TextField
          margin="dense"
          label={t('comms.description')}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={!!errors.description}
          helperText={errors.description || t('comms.descriptionHelp')}
          disabled={loading}
          placeholder={t('comms.descriptionPlaceholder')}
          multiline
          rows={2}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || !name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('comms.updating') : t('actions.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditChannelDialog; 